import type { User, UserRecord } from "./users.schema";

/**
 * Flagging threshold for member removal (total infractions)
 */
export const FLAGGING_THRESHOLD = 3;

/**
 * Calculates total infractions (absences + all cancellations) for a user
 */
export function getTotalInfractions(user: User | UserRecord): number {
    const absences = user.datesOfAbsence?.length ?? 0;
    const earlyCancellations = user.datesOfEarlyCancellation?.length ?? 0;
    const lateCancellations = user.datesOfLateCancellation?.length ?? 0;
    return absences + earlyCancellations + lateCancellations;
}

/**
 * Type guard to check if user has active access
 * User must have access granted and not revoked
 */
export function hasAccess(user: User): boolean {
    return !!user.accessGrantedAt && !user.accessRevokedAt;
}

/**
 * Type guard to check if user is flagged for removal
 */
export function isFlaggedForRemoval(user: User): boolean {
    return !!user.flaggedForRemovalAt;
}

/** True if the member has an active leave period (admin-set). */
export function isOnLeave(user: User | UserRecord): boolean {
    return !!user.leaveStartedAt;
}

/**
 * Type guard to check if user can be archived
 * User can be archived if they are waitlisted (no access granted) OR have had access revoked
 */
export function canArchiveMember(user: User | UserRecord): boolean {
    // Waitlisted: no accessGrantedAt
    const isWaitlisted = !user.accessGrantedAt;
    // Revoked: has accessRevokedAt
    const isRevoked = !!user.accessRevokedAt;

    return isWaitlisted || isRevoked;
}
